import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import * as R from 'ramda';
import moment from 'moment';
import { convertToRaw } from 'draft-js';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';

import RichContent from '../../UI/RichContent';
import SMDatePicker from '../../UI/SMDatePicker';
import SMTimePicker from '../../UI/SMTimePicker';
import SMWeekDayPicker from '../../UI/SMWeekDayPicker';
import { SURVEY_TYPE, SURVEY_HELP_TEXT, SUBMISSION_HELPER_TEXT, HIDE_CONTROLS, SHOW_CONTROLS } from './constants'
import { buildTime } from '../../utils/DateUtils';
import { sortOrgs, getTitle, getActionText, isEditAction, isOrganizationsDefined, getSharingOption } from './utils';
import { styles } from './styles';
import { FORM_ACTION, SharingOption } from '../FormSettings/constants';

function FormSettings (props) {
    const { classes, form, organizations, editorState: { editor, setEditor }, action, editable } = props;
    const [validated, setValidated] = useState(false);
    const [validDates, setValidDates] = useState(true);
    const [validTimes, setValidTimes] = useState(true);
    const [validDaysOfWeek, setValidDaysOfWeek] = useState(true);
    const [submissionOptions, setSubmissionOptions] = useState(HIDE_CONTROLS);
    const [submissionText, setSubmissionText] = useState(SUBMISSION_HELPER_TEXT.onetime);
    const [hasOrganization, setHasOrganization] = useState(true);

    useEffect(() => {
        form && initSurveyControls();
    });

    useEffect(() => {
        form && validate();
    }, [form]);

    const initSurveyControls = () => {
        if (form.surveyType === SURVEY_TYPE.onetime) {
            setSubmissionOptions(HIDE_CONTROLS);
            setSubmissionText(SUBMISSION_HELPER_TEXT.onetime);
        } else {
            setSubmissionOptions(SHOW_CONTROLS);
            setSubmissionText(SUBMISSION_HELPER_TEXT.daily);
        }
        const organizationsDefined = isOrganizationsDefined(organizations);
        setHasOrganization(organizationsDefined);
        form.publish = getSharingOption(form, organizationsDefined);
    }

    const validate = () => {
        const validTitle = !!form.name.trim().length;

        const validDates = form.startDate && form.endDate ? moment(form.startDate).isBefore(form.endDate) : true;
        setValidDates(validDates);

        const validTimes = form.startTime && form.endTime ? moment(buildTime(form.startTime)).isBefore(buildTime(form.endTime)) : true;
        setValidTimes(validTimes);

        const daysOfWeekValid = (! form.daysOfWeek) ? true : form.daysOfWeek.length > 0;
        setValidDaysOfWeek(daysOfWeekValid);

        setValidated(validTitle && validDates && validTimes && daysOfWeekValid && editable);
    }

    const updateDateField = (value) => {
        props.updateDateField(value);
        validate();
    }

    const updateDaysOfWeek = (value) => {
        props.updateDaysOfWeek(value);
        validate();
    }

    const handleRichContent = editorState => {
        setEditor(editorState);
        const value = convertToRaw(editorState.getCurrentContent());
        const target = {
            name: 'confirmationMsg',
            value
        };
        props.updateField({ target });
    };

    const updateSurveyType = (value) => {
        props.updateField(value);
    }

    const handleSave = () => {
        props.handleSave(action);
    }

    const handleClose = () => {
        props.handleClose(FORM_ACTION.cancel);
    }

    const sortedOrgs = sortOrgs(organizations);
    const isEditingAllowed = (editable && (!isEditAction(action))); // survey, track recipients, submission not allowed in edit mode
    const isSharingAllowed = (editable && hasOrganization);         // if no organization defined then no share allowed
    const subTitle = getActionText(action, editable);

    return form && (
        <Dialog
            open={props.dialogState.open}
            onClose={handleClose}
            fullWidth
        >
            <div className={classes.header}>
                <Typography variant="h6" color="inherit">{getTitle(action)}</Typography>
                <Typography variant="subtitle1" className={classes.subTitle}>{subTitle}</Typography>
            </div>
            <DialogContent className={classes.content}>
                <form className={classes.formContainer}>
                    <TextField
                        id="new-form-name"
                        name="name"
                        label="Form Name"
                        margin="normal"
                        InputLabelProps={{
                            shrink: true
                        }}
                        fullWidth
                        required
                        value={form.name}
                        onChange={props.updateField}
                        inputProps={{
                            maxLength: 128
                        }}
                        helperText={props.invalidName && props.invalidName}
                        error={!!props.invalidName}
                        disabled={!editable}
                    />
                    <FormHelperText className={classes.helperText}>Maximum characters: 128</FormHelperText>
                    <TextField
                        id="new-form-description"
                        name="description"
                        label="Description"
                        margin="normal"
                        InputLabelProps={{
                            shrink: true
                        }}
                        fullWidth
                        value={form.description}
                        onChange={props.updateField}
                        inputProps={{maxLength: 128}}
                        helperText="Maximum characters: 128"
                        disabled={!editable}
                    />
                    <div className={classes.activePeriodContainer}>
                        <Typography variant="subtitle2">Form Active Period</Typography>
                        <Grid container spacing={24}>
                            <Grid item xs={6}>
                                <SMDatePicker
                                    id="new-form-start"
                                    name="startDate"
                                    label="Start Date"
                                    margin="normal"
                                    helperText="If blank, form will be active immediately"
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    fullWidth
                                    value={form.startDate}
                                    onDateSelect={updateDateField}
                                    error={!validDates}
                                    disabled={!editable}
                                />
                            {!validDates && <FormHelperText error className={classes.helperText}>{SURVEY_HELP_TEXT.invalidDate}</FormHelperText>}
                            </Grid>
                            <Grid item xs={6}>
                                <SMDatePicker
                                    id="new-form-expiration"
                                    name="endDate"
                                    label="Expiration Date"
                                    margin="normal"
                                    helperText="If blank, form will never expire"
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    fullWidth
                                    value={form.endDate}
                                    onDateSelect={updateDateField}
                                    error={!validDates}
                                    disabled={!editable}
                                />
                            </Grid>
                        </Grid>
                    </div>

                    <div className={classes.activePeriodContainer}>
                        <Typography variant="subtitle2">Schedule</Typography>
                        <Grid container>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth margin="normal">
                                    <Select
                                        value={form.surveyType}
                                        inputProps={{
                                            name: "surveyType",
                                            id: "surveyType"
                                        }}
                                        onChange={updateSurveyType}
                                        disabled={!isEditingAllowed}
                                    >
                                        <MenuItem value={SURVEY_TYPE.onetime}>{SURVEY_TYPE.onetimeTitle}</MenuItem>
                                        <MenuItem value={SURVEY_TYPE.daily}>{SURVEY_TYPE.dailyTItle}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <div style={{display: submissionOptions}}>
                            <Grid container spacing={24}>
                                <Grid item xs={6}>
                                    <SMTimePicker
                                        name="startTime"
                                        label="Start Time"
                                        margin="normal"
                                        helperText={SURVEY_HELP_TEXT.beginTime}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        fullWidth
                                        value={form.startTime}
                                        onDateSelect={updateDateField}
                                        disabled={!editable}
                                    />
                                    {!validTimes && <FormHelperText error className={classes.helperText}>{SURVEY_HELP_TEXT.invalidTime}</FormHelperText>}
                                </Grid>
                                <Grid item xs={6}>
                                    <SMTimePicker
                                        name="endTime"
                                        label="End Time"
                                        margin="normal"
                                        helperText={SURVEY_HELP_TEXT.endTime}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        fullWidth
                                        value={form.endTime}
                                        onDateSelect={updateDateField}
                                        disabled={!editable}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={24}>
                                <Grid item xs={6}>
                                <FormHelperText className={classes.helperText} style={{padding: '5px 0 10px 0'}}>{SURVEY_HELP_TEXT.weekendPickerTitle}</FormHelperText>
                                <SMWeekDayPicker
                                    name="daysOfWeek"
                                    label="Day of week"
                                    value={form.daysOfWeek}
                                    onDaySelect={updateDaysOfWeek}
                                    error={!validDaysOfWeek}
                                    disabled={!editable}
                                />
                                {!validDaysOfWeek && <FormHelperText error className={classes.helperText} style={{padding: '10px 0 0 0'}}>{SURVEY_HELP_TEXT.weekendPickerError}</FormHelperText>}
                                </Grid>
                            </Grid>
                        </div>
                    </div>

                    <div className={classes.optionsContainer}>
                        <FormControl>
                            <Typography variant="subtitle2">Options</Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name='trackUser'
                                            checked={form.trackUser}
                                            onChange={props.updateField}
                                            disabled={!isEditingAllowed}
                                        />
                                    }
                                    label="Track recipient submission"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name='singleUserSubmission'
                                            checked={form.singleUserSubmission}
                                            onChange={props.updateField}
                                            disabled={!isEditingAllowed}
                                        />
                                    }
                                    label={submissionText}
                                />
                                {/* <FormControlLabel
                                    control={
                                        <Checkbox
                                            name='randomOrder'
                                            checked={form.randomOrder}
                                            onChange={props.updateField}
                                        />
                                    }
                                    label="Randomize question order"
                                /> */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name='disabled'
                                            checked={form.disabled}
                                            onChange={props.updateField}
                                            disabled={!editable}
                                        />
                                    }
                                    label="Disable form"
                                />
                            </FormGroup>
                        </FormControl>
                    </div>
                    <RichContent
                        editorState={editor}
                        onChange={handleRichContent}
                        placeholder="Enter the confirmation text that you would like to display to users when they submit their response"
                        disabled={!editable}
                    />
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel htmlFor="new-form-share">Share Form With</InputLabel>
                                <Select
                                    value={form.publish}
                                    inputProps={{
                                        name: "publish",
                                        id: "new-form-share"
                                    }}
                                    onChange={props.updateField}
                                    disabled={!isSharingAllowed}
                                >
                                    <MenuItem value={SharingOption.NO_SHARE}>Nobody</MenuItem>
                                    <MenuItem value={SharingOption.ANYONE}>Anyone</MenuItem>
                                    <MenuItem value={SharingOption.TOP_LEVEL_USERS}>Top-level Users</MenuItem>
                                    <MenuItem value={SharingOption.SPECIFIC_ORGANIZATIONS}>Specific Organizations</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {form.publish === SharingOption.SPECIFIC_ORGANIZATIONS && (
                            <Grid item xs={12} className={classes.specificOrgsContainer}>
                                <FormControl fullWidth margin="normal">
                                    <Typography variant="subtitle2">Specific Organizations</Typography>
                                    <FormGroup>
                                        {sortedOrgs.map((org, index) => {
                                            const organizationIds = form.organizationIds ? form.organizationIds : [];
                                            const checked = R.contains(org.id, organizationIds)
                                            return (
                                                <FormControlLabel
                                                    key={index}
                                                    control={
                                                        <Checkbox
                                                            value={org.id.toString()}
                                                            checked={checked}
                                                            onChange={props.updateOrganizations}
                                                            disabled={!isSharingAllowed}
                                                        />
                                                    }
                                                    label={org.name}
                                                />
                                        )})}
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={handleClose}>cancel</Button>
                <Button color="primary" onClick={handleSave} disabled={!validated}>
                    save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default withRouter(withStyles(styles)(FormSettings));
